import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, Input, Select, Textarea, DatePicker, RNLLoader, HeaderBack } from '../components/UI';
import { fmtNum } from '../utils';
import * as api from '../api';

export default function StandaloneQSEntryPage({ toast }) {
  const navigate = useNavigate();

  // Master Data
  const [projects, setProjects] = useState([]);
  const [costCategories, setCostCategories] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [users, setUsers] = useState([]);

  // Project Specific Data
  const [boqItems, setBoqItems] = useState([]);
  const [subsections, setSubsections] = useState([]);

  // State
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingProjectData, setLoadingProjectData] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({ installation_date: new Date().toISOString().slice(0, 10), unit_of_measure: 'Nr' });
  const [boqComponents, setBoqComponents] = useState([]);

  // Load Initial Master Data
  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [projRes, costCatRes, discRes, usersRes] = await Promise.all([
          api.listProjects(),
          api.listCategories(false).catch(() => []),
          api.listDisciplines().catch(() => []),
          api.listUsers().catch(() => [])
        ]);
        
        setProjects(Array.isArray(projRes) ? projRes : (projRes?.data || projRes?.items || []));
        setCostCategories(Array.isArray(costCatRes) ? costCatRes : []);
        setDisciplines(Array.isArray(discRes) ? discRes : (discRes?.data || discRes?.items || []));
        setUsers(Array.isArray(usersRes) ? usersRes : (usersRes?.data || usersRes?.items || []));
      } catch (e) {
        toast?.show?.('Failed to load master data', 'error');
      } finally {
        setLoadingInitial(false);
      }
    };
    loadInitial();
  }, [toast]);

  // Handle Project Selection Change
  useEffect(() => {
    if (!selectedProjectId) {
      setBoqItems([]);
      setSubsections([]);
      return;
    }
    
    const loadProjectData = async () => {
      setLoadingProjectData(true);
      try {
        const [subsRes, boqRes] = await Promise.all([
          api.listDmrSubsections(selectedProjectId).catch(() => []),
          api.listBOQ(selectedProjectId).catch(() => [])
        ]);
        setSubsections(Array.isArray(subsRes) ? subsRes : (subsRes?.data || subsRes?.items || []));
        setBoqItems(Array.isArray(boqRes) ? boqRes : (boqRes?.data || boqRes?.items || []));
      } catch (e) {
        toast?.show?.('Failed to load project-specific data', 'error');
      } finally {
        setLoadingProjectData(false);
      }
    };
    loadProjectData();
  }, [selectedProjectId, toast]);

  // Generate Installation Code once project is selected and form is empty
  useEffect(() => {
    if (selectedProjectId && !form.installation_code) {
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
      setForm(f => ({ ...f, installation_code: `INST-${dateStr}-${randomStr}` }));
    }
  }, [selectedProjectId, form.installation_code]);

  // Update BOQ Components when Main BOQ Item changes
  useEffect(() => {
    if (form.boq_item_id) {
      const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
      const selectedItem = itemsArray.find(i => String(i.boq_item_id) === String(form.boq_item_id) || String(i.id) === String(form.boq_item_id));
      if (selectedItem && selectedItem.components && selectedItem.components.length > 0) {
        setBoqComponents(selectedItem.components);
      } else {
        setBoqComponents([]);
      }
    } else {
      setBoqComponents([]);
    }
  }, [form.boq_item_id, boqItems]);

  // Frontend Calculations for Quantities
  useEffect(() => {
    const prev = parseFloat(form.previous_installed_qty) || 0;
    const curr = parseFloat(form.current_installed_qty) || 0;
    const totalInstalled = prev + curr;

    const issued = parseFloat(form.issued_qty) || 0;
    const remaining = issued - totalInstalled;

    if (form.total_installed_qty !== totalInstalled || form.remaining_qty !== remaining) {
      setForm(f => ({ 
        ...f, 
        total_installed_qty: totalInstalled,
        remaining_qty: remaining
      }));
    }
  }, [
    form.previous_installed_qty, 
    form.current_installed_qty, 
    form.issued_qty,
    form.total_installed_qty,
    form.remaining_qty
  ]);

  const saveTransaction = async () => {
    if (!selectedProjectId) {
      toast?.show?.('Please select a project first', 'error');
      return;
    }
    if (!form.boq_item_id || !form.dmr_subsection_id || !form.current_installed_qty) {
      toast?.show?.('Please fill in required fields: BOQ Item, Sub-section, Current Installed Qty', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, project_id: selectedProjectId };
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });
      
      await api.createDailyInstalled(payload);
      toast?.show?.('QS Installation entry created successfully');
      
      // Reset form but keep basic defaults
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
      setForm({ 
        installation_date: new Date().toISOString().slice(0, 10), 
        unit_of_measure: 'Nr', 
        installation_code: `INST-${dateStr}-${randomStr}` 
      });
    } catch (e) {
      toast?.show?.(e.message, 'error');
    }
    setSaving(false);
  };

  if (loadingInitial) {
    return <RNLLoader title="Loading Master Data..." subtitle="Please wait" />;
  }

  const sectionTitleStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em', marginBottom: 12, textTransform: 'uppercase' };
  const inputStyle = { background: '#f8fafc', color: '#1e293b', border: '1px solid #cbd5e1', padding: '10px 12px' };
  
  const boqItemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);

  return (
    <div className="fade-in" style={{ padding: '10px 0', maxWidth: '100%', margin: '0 auto', width: '100%' }}>
      {/* Back button moved to top header */}
      <HeaderBack onClick={() => navigate('/report-entry')} label="Back to Report Entry" />

      <div style={{ background: '#ffffff', border: '3px solid black', borderRadius: 22, padding: 18, boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ borderRadius: 12, overflow: 'hidden', background: '#ffffff', border: '1px solid black' }}>
        {/* PROJECT SELECTION BAR & TITLE */}
        <div style={{ background: '#0b318bff', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: '#ffffff', letterSpacing: '0.02em' }}>
            New QS Installation Entry
          </h2>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {loadingProjectData && <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, marginRight: 8 }}>Loading project data...</div>}
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap' }}>SELECT PROJECT:</div>
            <div style={{ width: 280 }}>
              <Select 
                value={selectedProjectId} 
                onChange={setSelectedProjectId}
                options={[{ value: '', label: '— Select Project Context —' }, ...projects.map(p => ({ value: p.project_id, label: p.project_name }))]} 
                style={{ background: '#fff', border: 'none', borderRadius: 8, height: 38 }}
              />
            </div>
          </div>
        </div>

        {!selectedProjectId ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏢</div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>No Project Selected</h3>
            <p>Please select a project from the dropdown above to continue with the entry.</p>
          </div>
        ) : (
          <div className="mobile-p-16" style={{ padding: '32px', background: '#ffffff' }}>
            
            {/* SECTION 1 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 1 — BOQ & CLASSIFICATION</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid black' }}>
                <Select label="MAIN BOQ ITEM" required value={form.boq_item_id || ''}
                  onChange={v => setForm(f => ({ ...f, boq_item_id: v, boq_component_id: '' }))}
                  style={inputStyle}
                  options={[{ value: '', label: '— Select BOQ Item —' }, ...boqItemsArray.map(i => ({ value: i.boq_item_id || i.id, label: `${i.boq_code || ''} — ${i.description}`.trim() }))]} />

                <Select label="SUB BOQ COMPONENT" value={form.boq_component_id || ''}
                  onChange={v => setForm(f => ({ ...f, boq_component_id: v }))}
                  style={inputStyle}
                  disabled={boqComponents.length === 0}
                  options={boqComponents.length === 0
                    ? [{ value: '', label: '— No Components Available —' }]
                    : [{ value: '', label: '— Select Component —' }, ...boqComponents.map(c => ({ value: c.component_id || c.id, label: c.description || c.name }))]
                  } />

                <Select label="COST COMPONENT" value={form.cost_category_id || ''}
                  onChange={v => setForm(f => ({ ...f, cost_category_id: v }))}
                  style={inputStyle}
                  options={[{ value: '', label: '— Select Cost Component —' }, ...(costCategories || []).map(c => ({ value: c.cost_category_id || c.id, label: c.name }))]} />
                  
                <Select label="SUB-SECTION" required value={form.dmr_subsection_id || ''}
                  onChange={v => setForm(f => ({ ...f, dmr_subsection_id: v }))} style={inputStyle}
                  options={[{ value: '', label: '— Select Sub-section —' }, ...subsections.map(b => ({ value: b.id, label: b.name }))]} />
                  
                <Select label="DISCIPLINE" required value={form.discipline_id || ''}
                  onChange={v => setForm(f => ({ ...f, discipline_id: v }))} style={inputStyle}
                  options={[{ value: '', label: '— Select Discipline —' }, ...disciplines.map(b => ({ value: b.discipline_id || b.id, label: b.name }))]} />
              </div>
            </div>

            {/* SECTION 2 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 2 — GENERAL DETAILS & LOCATION</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid black' }}>
                <DatePicker label="INSTALLATION DATE" required value={form.installation_date || ''}
                  onChange={v => setForm(f => ({ ...f, installation_date: v }))} style={inputStyle} />

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 'bold', color: '#000101ff', marginBottom: 4, textTransform: 'uppercase' }}>INSTALLATION ID</label>
                  <div style={{ background: '#e2e8f0', border: '2px dashed #081220ff', borderRadius: '6px', padding: '9px 12px', width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
                      {form.installation_code || 'Generating ID...'}
                    </span>
                  </div>
                </div>

                <Input label="BRAND NAME" value={form.brand_name || ''}
                  onChange={v => setForm(f => ({ ...f, brand_name: v }))} style={inputStyle} />
                  
                <Input label="LOCATION" value={form.location || ''}
                  onChange={v => setForm(f => ({ ...f, location: v }))} style={inputStyle} />
                  
                <Input label="ZONE/FLOOR" value={form.zone || ''}
                  onChange={v => setForm(f => ({ ...f, zone: v }))} style={inputStyle} />
                  
                <Input label="DESCRIPTION" value={form.description || ''}
                  onChange={v => setForm(f => ({ ...f, description: v }))} style={inputStyle} />
              </div>
            </div>

            {/* SECTION 3 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 3 — QUANTITIES</div>
              <div className="grid-cols-4" style={{ background: '#f0f4f8', padding: '20px', borderRadius: '12px', border: '1px solid black' }}>
                <Select label="UNIT OF MEASURE" required value={form.unit_of_measure || 'Nr'}
                  onChange={v => setForm(f => ({ ...f, unit_of_measure: v }))} style={inputStyle}
                  options={['M', 'M2', 'M3', 'Nr', 'KG'].map(s => ({ value: s, label: s }))} />
                  
                <Input label="BOQ QUANTITY" type="number" value={form.boq_quantity || ''}
                  onChange={v => setForm(f => ({ ...f, boq_quantity: v }))} style={inputStyle} />
                  
                <Input label="PURCHASED QTY" type="number" value={form.purchased_qty || ''}
                  onChange={v => setForm(f => ({ ...f, purchased_qty: v }))} style={inputStyle} />
                  
                <Input label="ISSUED QTY" type="number" value={form.issued_qty || ''}
                  onChange={v => setForm(f => ({ ...f, issued_qty: v }))} style={inputStyle} />

                <Input label="PREVIOUS INSTALLED" type="number" value={form.previous_installed_qty || ''}
                  onChange={v => setForm(f => ({ ...f, previous_installed_qty: v }))} style={inputStyle} />
                  
                <Input label="CURRENT INSTALLED" required type="number" value={form.current_installed_qty || ''}
                  onChange={v => setForm(f => ({ ...f, current_installed_qty: v }))} style={{ ...inputStyle, borderColor: 'var(--brand)', borderWidth: 2 }} />

                <div style={{ gridColumn: 'span 2', display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#1e293b', borderRadius: '8px', padding: '12px 16px', color: '#fff' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>TOTAL INSTALLED</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: '#10b981', fontFamily: 'var(--mono)' }}>{fmtNum(form.total_installed_qty || 0)}</span>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#1e293b', borderRadius: '8px', padding: '12px 16px', color: '#fff' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>REMAINING QTY</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: '#eab308', fontFamily: 'var(--mono)' }}>{fmtNum(form.remaining_qty || 0)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 4 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 4 — WASTAGE & OFFCUTS</div>
              <div className="grid-cols-4" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid black' }}>
                <Input label="OFFCUT QTY" type="number" value={form.offcut_qty || ''}
                  onChange={v => setForm(f => ({ ...f, offcut_qty: v }))} style={inputStyle} />
                <Input label="REUSABLE OFFCUT" type="number" value={form.reusable_offcut_qty || ''}
                  onChange={v => setForm(f => ({ ...f, reusable_offcut_qty: v }))} style={inputStyle} />
                <Input label="RETURNED QTY" type="number" value={form.returned_qty || ''}
                  onChange={v => setForm(f => ({ ...f, returned_qty: v }))} style={inputStyle} />
                <Input label="WASTAGE QTY" type="number" value={form.wastage_qty || ''}
                  onChange={v => setForm(f => ({ ...f, wastage_qty: v }))} style={inputStyle} />
                  
                <Input label="LOST QTY" type="number" value={form.lost_qty || ''}
                  onChange={v => setForm(f => ({ ...f, lost_qty: v }))} style={inputStyle} />
                <Input label="UNINSTALLED ISSUED" type="number" value={form.uninstalled_issued_qty || ''}
                  onChange={v => setForm(f => ({ ...f, uninstalled_issued_qty: v }))} style={inputStyle} />
                <Input label="VARIANCE QTY" type="number" value={form.variance_qty || ''}
                  onChange={v => setForm(f => ({ ...f, variance_qty: v }))} style={inputStyle} />
                <Input label="INSTALLATION %" type="number" value={form.installation_percentage || ''}
                  onChange={v => setForm(f => ({ ...f, installation_percentage: v }))} style={inputStyle} />
              </div>
            </div>

            {/* SECTION 5 */}
            <div>
              <div style={sectionTitleStyle}>SECTION 5 — VERIFICATION & REMARKS</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid black' }}>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1e293b', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.is_tested || false} onChange={e => setForm(f => ({ ...f, is_tested: e.target.checked }))} />
                    IS TESTED?
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1e293b', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.is_commissioned || false} onChange={e => setForm(f => ({ ...f, is_commissioned: e.target.checked }))} />
                    IS COMMISSIONED?
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1e293b', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.is_re_measured || false} onChange={e => setForm(f => ({ ...f, is_re_measured: e.target.checked }))} />
                    IS RE-MEASURED?
                  </label>
                </div>
                
                <Select label="SUPERVISOR" value={form.supervisor_id || ''}
                  onChange={v => setForm(f => ({ ...f, supervisor_id: v }))} style={inputStyle}
                  options={[{ value: '', label: '— Select Supervisor —' }, ...(users||[]).map(u => ({ value: u.user_id || u.id, label: `${u.first_name} ${u.last_name}` }))]} />
                  
                <Select label="QS EMPLOYEE" value={form.qs_employee_id || ''}
                  onChange={v => setForm(f => ({ ...f, qs_employee_id: v }))} style={inputStyle}
                  options={[{ value: '', label: '— Select QS Employee —' }, ...(users||[]).map(u => ({ value: u.user_id || u.id, label: `${u.first_name} ${u.last_name}` }))]} />
                  
                <Input label="INSTALLER NAME" value={form.installer_name || ''}
                  onChange={v => setForm(f => ({ ...f, installer_name: v }))} style={inputStyle} />
                  
                <Select label="QA/QC STATUS" value={form.qa_qc_status || ''}
                  onChange={v => setForm(f => ({ ...f, qa_qc_status: v }))} style={inputStyle}
                  options={[
                    { value: '', label: '— Select QA/QC Status —' },
                    { value: 'Pending', label: 'Pending' },
                    { value: 'Passed', label: 'Passed' },
                    { value: 'Failed', label: 'Failed' },
                  ]} />
                  
                <Select label="INSPECTION STATUS" value={form.inspection_status || ''}
                  onChange={v => setForm(f => ({ ...f, inspection_status: v }))} style={inputStyle}
                  options={[
                    { value: '', label: '— Select Inspection Status —' },
                    { value: 'Pending', label: 'Pending' },
                    { value: 'Passed', label: 'Passed' },
                    { value: 'Failed', label: 'Failed' },
                  ]} />
                  
                <div style={{ gridColumn: 'span 3' }}>
                  <Textarea label="REMARKS" value={form.remarks || ''}
                    onChange={v => setForm(f => ({ ...f, remarks: v }))}
                    rows={3} placeholder="Optional notes or description about the installation..."
                    style={{ ...inputStyle, width: '100%' }} />
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="mobile-form-footer flex-mobile-col" style={{ background: '#0b318bff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '32px -32px -32px -32px' }}>
              <Btn onClick={() => navigate('/report-entry')} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '10px 24px', fontWeight: 600, fontSize: 14 }}>
                Cancel
              </Btn>
              <Btn loading={saving} onClick={saveTransaction} disabled={!selectedProjectId} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '10px 24px', fontWeight: 600, fontSize: 14 }}>
                Save
              </Btn>
            </div>

          </div>
        )}
        </div>
      </div>
    </div>
  );
}
